// ⌘K's search (SEARCH-NOTIF; 06 §Shell "jump", extending M2-04): one `search.palette` call per
// query answers people, digital employees, seats, teams, metrics, priorities, boards, docs,
// channels, tasks and messages — each filtered on the server by its own list route's rule. The
// caller's recent picks (palette-recents.ts) go with the query and come back first, under
// "Recent"; with an empty query they are all the palette shows here. Groups follow the server's
// kind order, each row with its type's icon; cmdk gives arrow keys and Enter. Typing waits 150 ms;
// an API without the route (pending) shows nothing rather than an error.
import { Badge, CommandGroup, CommandItem } from "@companyos/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  ArmchairIcon,
  BotIcon,
  BrainIcon,
  CheckSquareIcon,
  FileTextIcon,
  FlagIcon,
  GaugeIcon,
  HashIcon,
  type LucideIcon,
  MessageSquareIcon,
  PaperclipIcon,
  SearchIcon,
  SquareKanbanIcon,
  UserRoundIcon,
  UsersRoundIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import type { Api } from "../api/client";
import { type CompanyAccess, isManager, isOperatorOnly } from "../features/company";
import { memorySearchQuery } from "../features/memory/memory-queries";
import { useFeature } from "../features/use-feature";
import { recentsQuery } from "../features/work/recents";
import { pendingAsNull } from "../features/work/work-queries";
import { readPaletteRecent, recordPaletteRecent } from "./palette-recents";
import { recentPair } from "./palette-work";

export type PaletteHit = NonNullable<
  Awaited<ReturnType<Api["client"]["search"]["palette"]>>
>["data"][number];
type Kind = PaletteHit["kind"];

export const KIND_META: Record<Kind, { heading: string; icon: LucideIcon }> = {
  person: { heading: "People", icon: UserRoundIcon },
  agent: { heading: "Digital employees", icon: BotIcon },
  seat: { heading: "Seats", icon: ArmchairIcon },
  team: { heading: "Teams", icon: UsersRoundIcon },
  metric: { heading: "Metrics", icon: GaugeIcon },
  priority: { heading: "Priorities", icon: FlagIcon },
  board: { heading: "Boards", icon: SquareKanbanIcon },
  doc: { heading: "Docs", icon: FileTextIcon },
  channel: { heading: "Channels", icon: HashIcon },
  task: { heading: "Tasks", icon: CheckSquareIcon },
  message: { heading: "Messages", icon: MessageSquareIcon },
  file: { heading: "Files", icon: PaperclipIcon },
};

function useDebounced(value: string, ms: number): string {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Where a hit opens (a route and its params). */
export function hitTarget(
  h: PaletteHit,
  company: CompanyAccess,
): { to: string; params: Record<string, string>; search?: Record<string, string> } {
  const slug = company.slug;
  const manager = isManager(company);
  switch (h.kind) {
    case "person":
    case "seat":
      return h.seat_id
        ? { to: "/c/$slug/kpis/chart/seat/$id", params: { slug, id: h.seat_id } }
        : manager
          ? { to: "/c/$slug/setup/people", params: { slug } }
          : { to: "/c/$slug/kpis/chart", params: { slug } };
    case "agent":
      return { to: "/c/$slug/agents/de/$id", params: { slug, id: h.id } };
    case "team":
      return manager
        ? { to: "/c/$slug/setup/teams", params: { slug } }
        : { to: "/c/$slug/kpis/chart", params: { slug } };
    case "metric":
      return manager
        ? { to: "/c/$slug/setup/metrics", params: { slug } }
        : { to: "/c/$slug/kpis/scorecard", params: { slug } };
    case "priority":
      return { to: "/c/$slug/kpis/priorities", params: { slug } };
    case "board":
      return { to: "/c/$slug/work/b/$boardId", params: { slug, boardId: h.id } };
    case "doc":
      return { to: "/c/$slug/work/p/$pageId", params: { slug, pageId: h.id } };
    case "channel":
      return { to: "/c/$slug/chat/$channelId", params: { slug, channelId: h.id } };
    case "task":
      return { to: "/c/$slug/work", params: { slug }, search: { task: h.id } };
    case "message":
      // CHAT-TOPICS-SEPARATE: a message of the channel's own conversation (no topic) opens it.
      return h.channel_id && h.topic_id
        ? {
            to: "/c/$slug/chat/$channelId/$topicId",
            params: { slug, channelId: h.channel_id, topicId: h.topic_id },
          }
        : h.channel_id
          ? { to: "/c/$slug/chat/$channelId", params: { slug, channelId: h.channel_id } }
          : { to: "/c/$slug/chat", params: { slug } };
    case "file":
      // TASK-FILES: a file opens its task (`open` looks the task up; this is the fallback).
      return h.board_id
        ? { to: "/c/$slug/work/b/$boardId", params: { slug, boardId: h.board_id } }
        : { to: "/c/$slug/work", params: { slug } };
  }
}

/** The groups in order: "Recent" first, then each kind (the server's order). */
export function groupHits(rows: readonly PaletteHit[]): { heading: string; hits: PaletteHit[] }[] {
  const recent = rows.filter((h) => h.recent);
  const groups: { heading: string; hits: PaletteHit[] }[] = recent.length
    ? [{ heading: "Recent", hits: recent }]
    : [];
  for (const kind of Object.keys(KIND_META) as Kind[]) {
    const hits = rows.filter((h) => !h.recent && h.kind === kind);
    if (hits.length) groups.push({ heading: KIND_META[kind].heading, hits });
  }
  return groups;
}

export function PaletteHits({
  api,
  company,
  query,
  onPick,
}: {
  api: Api;
  company: CompanyAccess;
  query: string;
  /** Runs the pick (the palette closes first). */
  onPick: (fn: () => void) => void;
}) {
  const q = useDebounced(query.trim(), 150);
  const navigate = useNavigate();
  const chat = useFeature("chat", company.id);
  const [recent, setRecent] = useState(() => readPaletteRecent(company.id));
  const typed = q.length >= 2;
  const hits = useQuery({
    queryKey: ["company", company.id, "palette", typed ? q : "", recent.join(",")],
    queryFn: () =>
      pendingAsNull(() =>
        api.client.search.palette({
          params: { co: company.id },
          query: {
            ...(typed ? { q } : {}),
            ...(recent.length ? { recent: recent.join(",") } : {}),
          },
        }),
      ),
    enabled: typed || recent.length > 0,
    staleTime: 10_000,
    placeholderData: keepPreviousData,
  });
  // WOS-BUNDLE: the server's recents lead an empty query (palette-work.tsx); a local pick they
  // already list is not repeated.
  const opened = useQuery({
    ...recentsQuery(api, company.id),
    enabled: !typed && !isOperatorOnly(company),
  });
  const shownAbove = new Set(typed ? [] : (opened.data ?? []).map(recentPair));
  // A one-letter query shows nothing from the server (the modes and pages above still filter).
  const rows = (query.trim().length === 1 ? [] : (hits.data?.data ?? [])).filter(
    (h) => !(h.recent && shownAbove.has(`${h.kind}:${h.id}`)),
  );
  // CHAT-SWITCH-A: the typed words on Chat's search page (all messages, with its filters).
  const words = query.trim();
  const searchRow =
    chat.enabled && words.length >= 2 ? (
      <CommandGroup heading="Search" forceMount>
        <CommandItem
          value={`palette search-messages ${words}`}
          forceMount
          data-kind="search-messages"
          onSelect={() =>
            onPick(
              () =>
                void navigate({
                  to: "/c/$slug/chat/search",
                  params: { slug: company.slug },
                  search: { q: words.slice(0, 200) },
                }),
            )
          }
        >
          <SearchIcon aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate">Search messages for “{words}”</span>
        </CommandItem>
      </CommandGroup>
    ) : null;
  // RECALL-PEOPLE-SURFACES: memory hits beside tasks and docs, labelled "from memory".
  const fromMemory = (
    <MemoryHits api={api} company={company} q={q} enabled={typed && q.length >= 3} />
  );
  if (!rows.length)
    return (
      <>
        {searchRow}
        {fromMemory}
      </>
    );

  const open = (h: PaletteHit) => {
    if (h.kind !== "message") setRecent(recordPaletteRecent(company.id, h.kind, h.id));
    if (h.kind === "file") {
      // A file hit names its board and task number; its task drawer opens by the task's id.
      void api.client.files
        .get({ params: { co: company.id, id: h.id } })
        .then((f) =>
          navigate({
            to: "/c/$slug/work" as never,
            params: { slug: company.slug } as never,
            search: { task: f.task_id ?? undefined } as never,
          }),
        )
        .catch(() => undefined);
      return;
    }
    const t = hitTarget(h, company);
    void navigate({
      to: t.to as never,
      params: t.params as never,
      ...(t.search ? { search: t.search as never } : {}),
    });
  };

  return (
    <>
      {searchRow}
      {groupHits(rows).map((g) => (
        <CommandGroup key={g.heading} heading={g.heading} forceMount>
          {g.hits.map((h) => {
            const Icon = KIND_META[h.kind].icon;
            return (
              <CommandItem
                key={`${g.heading}:${h.kind}:${h.id}`}
                value={`palette ${h.kind} ${h.id}`}
                forceMount
                data-kind={h.kind}
                onSelect={() => onPick(() => open(h))}
              >
                <Icon aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate">
                  {h.title}
                  {h.subtitle ? (
                    <span className="ml-2 text-muted-foreground">{h.subtitle}</span>
                  ) : null}
                </span>
                <span className="sr-only">{KIND_META[h.kind].heading}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>
      ))}
      {fromMemory}
    </>
  );
}

/**
 * Memory records that match the words, after the other results, each marked "from memory". Enter
 * (or a click) opens the record's words in place with its source one click away; a memory that
 * is off, not connected or not set up for the person shows nothing here (never an error row).
 */
function MemoryHits({
  api,
  company,
  q,
  enabled,
}: {
  api: Api;
  company: CompanyAccess;
  q: string;
  enabled: boolean;
}) {
  const memory = useFeature("memory", company.id);
  const navigate = useNavigate();
  const [open, setOpen] = useState<string | null>(null);
  const found = useQuery({
    ...memorySearchQuery(api, company.id, "company", q),
    enabled: enabled && memory.enabled && !isOperatorOnly(company),
  });
  const hits = found.data?.recall.state === "connected" ? found.data.hits.slice(0, 5) : [];
  if (hits.length === 0) return null;
  return (
    <CommandGroup heading="From memory" forceMount data-slot="palette-memory">
      {hits.map((h) => {
        const id = `${h.type}/${h.title}`;
        const source = memorySource(h.scope, company);
        return (
          <div key={id}>
            <CommandItem
              value={`palette memory ${id}`}
              forceMount
              data-kind="memory"
              aria-expanded={open === id}
              onSelect={() => setOpen(open === id ? null : id)}
            >
              <BrainIcon aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate">
                {h.title}
                <span className="ml-2 text-muted-foreground">{h.type}</span>
              </span>
              <Badge variant="neutral">from memory</Badge>
            </CommandItem>
            {open === id ? (
              <div
                className="mx-2 mb-1 rounded-md bg-muted/50 px-3 py-2 text-[13px]"
                data-slot="palette-memory-record"
              >
                <p>{h.preview}</p>
                {h.why ? <p className="mt-1 text-muted-foreground">Why: {h.why}</p> : null}
                {source ? (
                  <button
                    type="button"
                    className="mt-1 text-[13px] font-medium underline underline-offset-2"
                    onClick={() =>
                      void navigate({
                        to: source.to as never,
                        params: source.params as never,
                        ...(source.search ? { search: source.search as never } : {}),
                      })
                    }
                  >
                    Open source: {h.scope.name}
                  </button>
                ) : (
                  <p className="mt-1 text-muted-foreground">Source: {h.scope.name}</p>
                )}
              </div>
            ) : null}
          </div>
        );
      })}
    </CommandGroup>
  );
}

/** Where a memory hit's source lives, when it is a place CompanyOS can open. */
function memorySource(
  scope: { kind: string; id: string },
  company: CompanyAccess,
): { to: string; params: Record<string, string>; search?: Record<string, string> } | null {
  const slug = company.slug;
  switch (scope.kind) {
    case "task":
      return { to: "/c/$slug/work", params: { slug }, search: { task: scope.id } };
    case "seat":
      return { to: "/c/$slug/kpis/chart/seat/$id", params: { slug, id: scope.id } };
    case "de":
      return { to: "/c/$slug/agents/de/$id", params: { slug, id: scope.id } };
    case "space":
      return { to: "/c/$slug/work", params: { slug } };
    default:
      return null;
  }
}
